import { useMemo, useState } from 'react'

type SortFunction<T> = (a: T, b: T) => number
type DefaultOrder = 'keep' | 'reverse'

interface UseLocalTableSortOptions {
  readonly defaultOrder?: DefaultOrder
}

export function useLocalTableSort<T>(
  items: T[],
  sortFunctions: Partial<Record<string, SortFunction<T>>>,
  options: UseLocalTableSortOptions = {}
) {
  const { defaultOrder = 'keep' } = options

  const [sort, setSort] = useState<string[]>([])

  const sortedItems = useMemo(() => {
    if (!sort.length) {
      return defaultOrder === 'reverse' ? [...items].reverse() : [...items]
    }

    return [...items].sort((a, b) => {
      for (const field of sort) {
        const isDesc = field.startsWith('-')
        const key = isDesc ? field.slice(1) : field
        const sortFunction = sortFunctions[key]

        if (!sortFunction) continue

        const comparison = sortFunction(a, b)
        if (comparison !== 0) return isDesc ? -comparison : comparison
      }

      return 0
    })
  }, [items, sort, sortFunctions, defaultOrder])

  return {
    sortedItems,
    sort,
    onSortChange: setSort,
  }
}
